// has587 — shared state for the Scholars Opt-In "new applicant" notification.
//
// The notification bell + count was previously computed inline on the Opt-In List report
// (ScholarsAlternateList). has587 moves the bell to the navbar (visible app-wide, in Scholars scope,
// to Scholars admins / program managers) and adds an on/off toggle beside it. Because the bell now
// lives in a global component while the report still needs to acknowledge/sort, the logic is lifted
// here so both surfaces share one source of truth.
//
// Two independent pieces of browser-persisted state (best-effort localStorage, mirrors
// preferredProgramSide.js / the original ScholarsAlternateList seen-set — any failure degrades to a
// safe default rather than throwing):
//   1. SEEN set (per term): applicant ids the user has already acknowledged. "New" = current ids not
//      in the seen set. Acknowledging writes the current ids into the seen set, clearing the count.
//   2. ENABLED flag: the navbar toggle. ON (default) = the bell badges when new applicants arrive;
//      OFF = the bell stays visible but shows no badge/count.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetScholarsAlternateListQuery } from '../../services/graphql/api';

// --- seen-set (per term) ----------------------------------------------------
// Key prefix preserved from the original ScholarsAlternateList implementation so a user's existing
// acknowledgements survive this refactor (no spurious "new" flags on first load after deploy).
export const SEEN_KEY_PREFIX = 'hs.scholarsOptInSeen';

// --- enabled flag (navbar toggle) -------------------------------------------
export const ENABLED_KEY = 'hs.scholarsOptInNotifyEnabled';

export function seenStorageKey(term) { return `${SEEN_KEY_PREFIX}.${term ?? 'current'}`; }

export function readSeenIds(term) {
    try {
        const raw = localStorage.getItem(seenStorageKey(term));
        const arr = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(arr) ? arr : []);
    } catch (e) {
        console.error('Error reading Opt-In seen ids from localStorage:', e);
        return new Set();
    }
}

export function writeSeenIds(term, idSet) {
    try {
        localStorage.setItem(seenStorageKey(term), JSON.stringify(Array.from(idSet)));
        notifyOptInChange();
        return true;
    } catch (e) {
        console.error('Error writing Opt-In seen ids to localStorage:', e);
        return false;
    }
}

// --- cross-instance sync ----------------------------------------------------
// The navbar badge and the report page each call useScholarsOptInNotification separately, so they hold
// independent seen-set / enabled state. localStorage isn't reactive, so when one instance writes (e.g.
// the report acknowledges new applicants, or the toggle flips) we dispatch a lightweight in-tab event
// that every mounted instance subscribes to, keeping the nav badge in sync with the report immediately.
const OPT_IN_CHANGE_EVENT = 'hs:scholarsOptInChange';

function notifyOptInChange() {
    try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new Event(OPT_IN_CHANGE_EVENT));
        }
    } catch (e) {
        // Non-fatal: cross-instance sync is a convenience; state still corrects on next mount.
        console.error('Error dispatching Opt-In change event:', e);
    }
}

/**
 * Read the notification on/off preference. Defaults to ON (true) when unset or on any read failure —
 * the notification is opt-out, not opt-in.
 * @returns {boolean}
 */
export function readNotifyEnabled() {
    try {
        // Only an explicit "false" disables it; absent/anything-else => enabled (default ON).
        return localStorage.getItem(ENABLED_KEY) !== 'false';
    } catch (e) {
        console.error('Error reading Opt-In notify-enabled flag from localStorage:', e);
        return true;
    }
}

/**
 * Persist the notification on/off preference.
 * @param {boolean} enabled
 * @returns {boolean} true when written.
 */
export function writeNotifyEnabled(enabled) {
    try {
        localStorage.setItem(ENABLED_KEY, enabled ? 'true' : 'false');
        notifyOptInChange();
        return true;
    } catch (e) {
        console.error('Error writing Opt-In notify-enabled flag to localStorage:', e);
        return false;
    }
}

/**
 * Shared hook powering the Opt-In notification on both the navbar bell and the report.
 *
 * @param {object} [options]
 * @param {boolean} [options.active=true] - whether to actually run the underlying query. The navbar
 *        passes false when the user is out of Scholars scope / lacks the role so no request fires.
 * @returns {{
 *   items: object[], isFetching: boolean, refetch: Function, term: string,
 *   currentIds: string[], newCount: number, acknowledgeNew: Function,
 *   enabled: boolean, setEnabled: Function,
 * }}
 */
export function useScholarsOptInNotification({ active = true } = {}) {
    // RTK Query dedupes by endpoint+args, so the navbar and the report share one cache entry — the
    // query runs once even though both call this hook. `skip` keeps the navbar from firing it when the
    // controls aren't shown.
    const { data, isFetching, refetch } = useGetScholarsAlternateListQuery(
        undefined, { refetchOnMountOrArgChange: true, skip: !active }
    );

    const items = useMemo(() => data?.items ?? [], [data?.items]);
    // The list is global for the current term; derive the term from the rows for the per-term seen key.
    const term = items[0]?.term ?? 'current';

    // Snapshot the seen-set once per term so newly-arrived ids stay flagged until the user acknowledges.
    const [seenIds, setSeenIds] = useState(() => readSeenIds(term));
    useEffect(() => { setSeenIds(readSeenIds(term)); }, [term]);

    // Navbar on/off toggle; persisted, defaults ON. localStorage isn't reactive, so mirror in state.
    const [enabled, setEnabledState] = useState(() => readNotifyEnabled());
    const setEnabled = useCallback((next) => {
        const value = !!next;
        writeNotifyEnabled(value);
        setEnabledState(value);
    }, []);

    // Keep separate hook instances (navbar badge + report page) in sync: when any instance writes the
    // seen-set or the enabled flag it dispatches OPT_IN_CHANGE_EVENT; here we re-read both so, e.g.,
    // acknowledging on the report immediately clears the nav badge without needing a remount.
    useEffect(() => {
        const sync = () => {
            setSeenIds(readSeenIds(term));
            setEnabledState(readNotifyEnabled());
        };
        window.addEventListener(OPT_IN_CHANGE_EVENT, sync);
        return () => window.removeEventListener(OPT_IN_CHANGE_EVENT, sync);
    }, [term]);

    const currentIds = useMemo(
        () => items.map((i) => i.applicantEmployeeId).filter(Boolean),
        [items]
    );
    const newCount = useMemo(
        () => currentIds.filter((id) => !seenIds.has(id)).length,
        [currentIds, seenIds]
    );

    // Acknowledge: mark every currently-listed applicant as seen (clears the count).
    const acknowledgeNew = useCallback(() => {
        const next = new Set(currentIds);
        writeSeenIds(term, next);
        setSeenIds(next);
    }, [currentIds, term]);

    return { items, isFetching, refetch, term, currentIds, newCount, acknowledgeNew, enabled, setEnabled };
}
